import React, { useState, useEffect, useRef, useCallback } from "react";
import { FiRefreshCcw, FiChevronDown, FiX, FiSearch } from "react-icons/fi";
import "./NewCustomAsyncSelectDropdown.css";

const NewCustomAsyncSelectDropdown = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select options...",
  onRefresh,
  onInputChange,
  onMenuScrollToBottom,
  onMenuOpen,
  isLoading = false,
  isSearchable = true,
  isMulti = true,
  isDisabled = false,
  loadingMessage = "Loading...",
  noOptionsMessage = "No options found",
  noSearchResultsMessage = "No results found",
  // The colour for selection and highlight. Defaults to what every
  // existing page already gets; a page on a different palette passes
  // its own rather than fighting these inline styles from a stylesheet.
  /* Defaults live in the stylesheet as --nas-accent / --nas-accent-soft
     so one edit re-skins every picker. These props stay for the callers
     that pass an explicit colour. */
  accentColor = "var(--nas-accent)",
  accentSoftColor = "var(--nas-accent-soft)",
  // Type straight into the control instead of into a box inside the
  // open panel. Opt-in, so existing pages keep the panel search.
  searchInControl = false,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedValues, setSelectedValues] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);
  const menuRef = useRef(null);
  const optionsRef = useRef(null);
  // The option count the list last asked the page to extend from. Guards
  // the auto-fetch below against asking twice for the same batch.
  const autoPageRef = useRef(-1);
  const debounceTimeoutRef = useRef(null);
  const selectedContainerRef = useRef(null);

  useEffect(() => {
    if (Array.isArray(value)) {
      setSelectedValues(value);
    } else {
      setSelectedValues([]);
    }
  }, [value]);

  // Filter options based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredOptions(filtered);
    }
    setHighlightedIndex(-1);
  }, [searchTerm, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle search input with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      if (onInputChange && typeof onInputChange === "function") {
        onInputChange(value);
      }
    }, 500);
  };

  // Handle option selection
  const handleSelectOption = (option) => {
    let newSelectedValues;

    if (isMulti) {
      const isAlreadySelected = selectedValues.some(
        (item) => item.value === option.value,
      );

      if (isAlreadySelected) {
        newSelectedValues = selectedValues.filter(
          (item) => item.value !== option.value,
        );
      } else {
        newSelectedValues = [...selectedValues, option];
      }
    } else {
      newSelectedValues = [option];
      setIsOpen(false);
    }

    setSelectedValues(newSelectedValues);

    if (onChange) {
      onChange(newSelectedValues);
    }

    if (!isMulti) {
      setSearchTerm("");
    }
  };

  // Remove selected option
  const handleRemoveOption = (optionValue, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (isDisabled) return;
    const newSelectedValues = selectedValues.filter(
      (item) => item.value !== optionValue,
    );

    setSelectedValues(newSelectedValues);

    if (onChange) {
      onChange(newSelectedValues);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  // A batch of options can be shorter than the box that holds them -- ten
  // rows in a 250px panel, or a search that matched three. Nothing
  // overflows, so onScroll never fires and the list is stuck on its first
  // page however far you drag. Ask for the next batch directly whenever
  // the box cannot scroll. Keyed on the option count, so a fetch that
  // brings nothing new ends the chain instead of looping.
  useEffect(() => {
    if (!isOpen || isLoading) return;
    if (typeof onMenuScrollToBottom !== "function") return;
    // Nothing on screen yet is the page's first fetch, not a short list:
    // asking now would step over page one and start at page two.
    if (filteredOptions.length === 0) return;
    if (autoPageRef.current === filteredOptions.length) return;
    const box = optionsRef.current;
    if (!box || box.scrollHeight > box.clientHeight) return;
    // Only for a list that is meant to scroll. A dialog can turn this box
    // into a plain block that grows with its content (.att-embed does);
    // there "it does not scroll" is the design, not a short page, and
    // fetching on it would walk the whole table one batch at a time.
    const flow = window.getComputedStyle(box).overflowY;
    if (flow !== "auto" && flow !== "scroll") return;
    autoPageRef.current = filteredOptions.length;
    onMenuScrollToBottom();
  }, [isOpen, isLoading, filteredOptions.length, onMenuScrollToBottom]);

  // A fresh open starts the chain over -- the options may have been
  // replaced by a search or a refresh since the last time it ran.
  useEffect(() => {
    if (!isOpen) autoPageRef.current = -1;
  }, [isOpen]);

  // Handle scroll for infinite loading
  const handleMenuScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      if (onMenuScrollToBottom && !isLoading) {
        onMenuScrollToBottom();
      }
    }
  };

  // Handle refresh
  const handleRefresh = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSearchTerm("");
    if (onRefresh) {
      onRefresh();
    }
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    if (isDisabled) return;
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    if (nextOpen) {
      // Call onMenuOpen when dropdown opens
      if (onMenuOpen && typeof onMenuOpen === "function") {
        onMenuOpen();
      }
      if (searchInputRef.current) {
        setTimeout(() => searchInputRef.current?.focus(), 0);
      }
    }
  };

  // Determine what to show in the dropdown
  const renderDropdownContent = () => {
    // Case 1: Loading and no options yet (initial load)
    if (isLoading && options.length === 0) {
      return (
        <div className="nas-state">
          <span className="nas-spinner" role="status">
            <span className="nas-sr">Loading...</span>
          </span>
          <span>{loadingMessage}</span>
        </div>
      );
    }
    
    // Case 2: Not loading and no options available
    if (!isLoading && options.length === 0) {
      return (
        <div className="nas-state">
          {searchTerm ? noSearchResultsMessage : noOptionsMessage}
        </div>
      );
    }
    
    // Case 3: We have options but filtered results are empty
    if (filteredOptions.length === 0 && options.length > 0) {
      return (
        <div className="nas-state">
          {noSearchResultsMessage}
        </div>
      );
    }
    
    // Case 4: We have filtered options, show them
    if (filteredOptions.length > 0) {
      return (
        <>
          {filteredOptions.map((option, index) => {
            const isSelected = selectedValues.some(
              (item) => item.value === option.value,
            );
            const isHighlighted = index === highlightedIndex;
 
            return (
              <div
                key={option.value}
                className={`nas-option${isHighlighted ? " is-active" : ""}${
                  isSelected ? " is-selected" : ""
                }`}
                onClick={() => handleSelectOption(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className="nas-option-row">
                  {/* Only show checkbox for multi-select */}
                  {isMulti && (
                    <span className={`nas-check${isSelected ? " is-on" : ""}`}>
                      {isSelected && <span className="nas-check-tick" />}
                    </span>
                  )}

                  <span className="nas-option-label">{option.label}</span>
                </div>
              </div>
            );
          })}
          
          {/* Show loading indicator at bottom when loading more */}
          {isLoading && filteredOptions.length > 0 && (
            <div className="nas-more">
              <span className="nas-spinner" role="status">
                <span className="nas-sr">Loading more...</span>
              </span>
            </div>
          )}
        </>
      );
    }
    
    return null;
  };
 
  return (
    // position/width came from Bootstrap's .position-relative and .w-100.
    // This project no longer loads Bootstrap, so both were doing nothing:
    // the menu below had no positioned ancestor and fell into the flow,
    // pushing the rest of the page down every time it opened.
    <div className="nas-root" ref={wrapperRef}>
      {/* Main dropdown container */}
      <div
        className={`form-control p-0 ${isDisabled ? 'bg-light text-muted' : ''}`}
        style={{
          minHeight: "42px",
          width: "100%",
          cursor: isDisabled ? "not-allowed" : "pointer",
          backgroundColor: isDisabled ? "var(--line)" : "var(--card)",
          // .form-control carried the border width and style; only the
          // colour was ever set here, so the control drew no border at all.
          border: "1px solid var(--line)",
          color: "var(--ink)",
          borderRadius: "12px",
          display: "flex",
          alignItems: "center",
          // The chevron is positioned against this, not the outer
          // wrapper, so it tracks the control's own box.
          position: "relative",
          ...rest.style,
        }}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        {/* Container for selected items with horizontal scroll */}
        <div
          ref={selectedContainerRef}
          className="nas-chips"
          style={{
            // .d-flex / .w-100 / .h-100 are Bootstrap and this project
            // deleted it, so this was never a flex row: every chip laid
            // out as a block and the control grew a line per selection.
            display: "flex",
            alignItems: "center",
            width: "100%",
            padding: "6px 40px 6px 12px",
            minHeight: "40px",
            gap: "6px",
            overflowX: "auto",
            overflowY: "hidden",
            whiteSpace: "nowrap",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {/* Scoped to this row. It used to be `div::-webkit-scrollbar`,
              which is not scoped to anything -- it hid the horizontal
              scrollbar of every div on any page that rendered one of
              these. */}

          {selectedValues.length > 0 ? (
            <>
              {selectedValues.map((item) => {
                if (!item || !item.label) return null;
 
                return (
                  <div
                    key={item.value}
                    className="d-inline-flex align-items-center"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      flexShrink: 0,
                      padding: "4px 10px",
                      backgroundColor: "var(--paper)",
                      border: "1px solid var(--line)",
                      borderRadius: "8px",
                      fontSize: "0.8125rem",
                      color: "var(--ink)",
                      marginRight: "4px",
                    }}
                  >
                    <span
                      style={{
                        maxWidth: "180px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.label}
                    </span>
                    <FiX
                      size={12}
                      className="nas-chip-x"
                      onClick={(e) => handleRemoveOption(item.value, e)}
                      style={{
                        color: "var(--ink-2)",
                        flexShrink: 0,
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.color = "var(--berry)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.color = "var(--ink-2)")
                      }
                    />
                  </div>
                );
              })}
            </>
          ) : searchInControl ? null : (
            <span
              className="nas-placeholder"
              style={{ fontSize: "0.875rem", color: "var(--ink-3)" }}
            >
              {placeholder}
            </span>
          )}

          {/* The query is typed here rather than in the open panel, so the
              control is the one place you look. Only rendered when the page
              opts in; the panel keeps its own box otherwise. */}
          {searchInControl && (
            <input
              ref={searchInputRef}
              type="text"
              className="nas-inline-input"
              value={searchTerm}
              placeholder={selectedValues.length > 0 ? "" : placeholder}
              onChange={handleSearchChange}
              onClick={(e) => {
                e.stopPropagation();
                if (!isOpen) toggleDropdown();
              }}
              onKeyDown={handleKeyDown}
              style={{
                flex: "1 1 60px",
                minWidth: "40px",
                border: 0,
                outline: "none",
                background: "transparent",
                font: "inherit",
                fontSize: "0.875rem",
                color: "var(--ink)",
                padding: 0,
              }}
            />
          )}
 
        </div>
        {/* Dropdown icon. A sibling of the chip row, not a child
            of it: the chip row scrolls sideways, and an absolutely
            positioned child of a scroll container scrolls and clips
            with it. */}
        <div
          className="nas-actions"
          style={{
            // .d-flex is Bootstrap and this project deleted it, so this
            // was a full-height block with the icon stuck at its top
            // rather than centred against the control.
            display: "flex",
            alignItems: "center",
            gap: "6px",
            position: "absolute",
            right: "10px",
            top: "1px",
            bottom: "1px",
            // Opaque, not transparent: the chip row scrolls sideways
            // underneath and used to run out from behind the chevron.
            // Inherited, not var(--card): that token is #FFFFFF only on
            // the light skin (#141937 dark, #0D0C1D indigo), so it drew a
            // dark block over a control a page had styled white.
            backgroundColor: "inherit",
            paddingLeft: "8px",
            pointerEvents: "none",
            borderRadius: "0 7px 7px 0",
          }}
        >
          {isLoading && (
            <div
              className="nas-spinner"
              role="status"
              style={{
                // .spinner-border drew nothing, so this was an empty
                // box, and .visually-hidden was inert so its "Loading..."
                // text showed as words inside the control. The ring's
                // size and shape come from .nas-spinner, which pins its
                // box so it cannot spin as an oval; only the colour is
                // this control's.
                borderTopColor: accentColor,
              }}
            >
              <span
                className="nas-sr"
                style={{
                  position: "absolute",
                  width: "1px",
                  height: "1px",
                  overflow: "hidden",
                  clip: "rect(0,0,0,0)",
                  whiteSpace: "nowrap",
                }}
              >
                Loading...
              </span>
            </div>
          )}
          <FiChevronDown
            className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
            style={{
              flex: "0 0 auto",
              transition: "transform .2s",
              // .rotate-180 is Bootstrap's too, so the chevron never
              // turned when the panel opened.
              transform: isOpen ? "rotate(180deg)" : "none",
              color: "var(--ink-2)",
            }}
          />
        </div>
      </div>
 
      {/* Dropdown menu */}
      {isOpen && (
        <div
          ref={menuRef}
          className="nas-menu"
          style={{
            // .dropdown-menu supplied position, display, width and border.
            // Without it the `top: 100%` below had nothing to act on and
            // the menu laid out in the flow instead of over the page.
            position: "absolute",
            // A column, so the options box below gets what the search row
            // leaves of the 300px. As a block it kept its own 250px under
            // the ~50px search row, ran past the bottom, and the hidden
            // overflow here cut the last option off where no scroll reached.
            display: "flex",
            flexDirection: "column",
            width: "100%",
            marginTop: "4px",
            zIndex: 1060,
            maxHeight: "300px",
            overflow: "hidden",
            boxShadow: "var(--shadow)",
            top: "100%",
            left: 0,
            padding: "8px",
            backgroundColor: "var(--card)",
            border: "1px solid var(--line)",
            borderRadius: "12px",
          }}
        >
          {/* Search input. Skipped when the control carries it. */}
          {isSearchable && !searchInControl && (
            <div className="nas-search">
              {/* One control, not three boxes with their own borders:
                  the row owns the border and the field inside it has
                  none, so icon, input and refresh read as a unit and
                  the focus ring belongs to the whole thing. */}
              <div className="nas-search-group">
                <span className="nas-search-icon">
                  <FiSearch size={14} />
                </span>
                <input
                  ref={searchInputRef}
                  type="text"
                  className="nas-search-input"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                {onRefresh && (
                  <button
                    type="button"
                    className="nas-search-refresh"
                    onClick={(e) => handleRefresh(e)}
                    title="Refresh"
                    aria-label="Refresh options"
                  >
                    <FiRefreshCcw size={14} />
                  </button>
                )}
              </div>
            </div>
          )}
 
          {/* Options list.

              overflowY is set here, not left to .overflow-auto: that is a
              Bootstrap class and this project deleted Bootstrap, so the
              box never scrolled. onScroll therefore never fired, and
              onMenuScrollToBottom with it -- which is what pages use to
              fetch the next page of options. Every one of these dropdowns
              was stuck on its first batch. */}
          <div
            ref={optionsRef}
            className="overflow-auto nas-options"
            style={{ maxHeight: "250px", overflowY: "auto", flex: "1 1 auto", minHeight: 0 }}
            onScroll={handleMenuScroll}
          >
            {renderDropdownContent()}
          </div>
        </div>
      )}
    </div>
  );
};

export default NewCustomAsyncSelectDropdown;
